// src/components/quizzes/BonusMultiplyDecimals.js
//
// Multiply decimals bonus round: catch the product.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell from "./kit/BonusShell";

const SPAWN_MS = 500;
const MIN_TARGETS_ONSCREEN = 2;    // at least this many correct options on screen
const TARGET_SPEED_SLOWDOWN = 0.9; // correct values fall a bit slower

/** ======= DECIMAL HELPERS ======= */
const randomInt = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Make one-decimal numbers 0.1 .. 0.9 (you can expand easily)
const makeOperand = () => {
  const A = randomInt(1, 9); // 1..9
  return { A, val: A / 10 };
};

// Format product exactly as a tidy decimal string
// We compute as exact fraction (A*B / 100) then render.
const formatProduct = (A, B) => {
  const num = A * B; // numerator over 100
  // Convert num/100 to string with at least 2 decimals, then trim trailing zeros
  let s = (num / 100).toFixed(3); // 0.12 -> "0.120"
  s = s.replace(/\.?0+$/,"");     // "0.120" -> "0.12", "0.100" -> "0.1", "1.000" -> "1"
  return s;
};

const asNumber = (s) => {
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

const nearlyEqual = (a, b, eps = 1e-9) => Math.abs(a - b) < eps;

/** Problem:
 * { a:{A,val}, b:{A,val}, productStr, productNum }
 */
const makeRandomProblem = () => {
  const a = makeOperand();
  const b = makeOperand();
  const productStr = formatProduct(a.A, b.A);
  const productNum = Number((a.val * b.val).toFixed(6));
  return { a, b, productStr, productNum };
};

const formatProblem = (p) => `${p.a.val.toFixed(1).replace(/\.0$/,".0")} × ${p.b.val.toFixed(1).replace(/\.0$/,".0")} = ____`;
const ruleText = () => `the correct product as a decimal`;

/** Build plausible distractors around decimal mistakes */
const makeDistractors = (p) => {
  const { a, b, productNum } = p;
  const prodRaw = a.A * b.A; // product over 100

  const uni = new Set();

  const push = (x) => {
    // x is a number; convert to trimmed string
    let s = x.toFixed(3).replace(/\.?0+$/,"");
    if (!nearlyEqual(asNumber(s), productNum)) uni.add(s);
  };

  // 1) Misplaced decimal point (common errors)
  // (A*B)/10 or /1000
  push(prodRaw / 10);    // too big by ×10
  push(prodRaw / 1000);  // too small by ÷10

  // 2) Rounding errors
  push(Number((productNum + 0.01).toFixed(2)));
  push(Number((productNum - 0.01).toFixed(2)));

  // 3) Multiply as whole numbers then place one decimal (wrong logic)
  push((a.A * b.A) / 10);

  // 4) Add operands instead of multiply
  push(Number((a.val + b.val).toFixed(2)));

  // 5) One operand times an integer approximation
  push(Number((a.val * b.A).toFixed(2)));
  push(Number((b.val * a.A).toFixed(2)));

  // 6) Near-misses
  push(Number((productNum + 0.02).toFixed(2)));
  push(Number((productNum - 0.02).toFixed(2)));

  // 7) One of the operands as a “trap”
  push(Number(a.val.toFixed(1)));
  push(Number(b.val.toFixed(1)));

  return Array.from(uni);
};

/** Keep mix balanced and ensure some correct are present */
const shouldSpawnCorrectBalanced = (problem, items) => {
  const correct = items.filter(it => nearlyEqual(asNumber(it.value), problem.productNum)).length;
  const wrong = items.length - correct;
  if (correct < MIN_TARGETS_ONSCREEN) return true;
  if (correct < wrong) return true;
  if (wrong < correct) return false;
  return Math.random() < 0.5;
};

const newProblem = () => {
  const p = makeRandomProblem();
  return { ...p, distractors: makeDistractors(p) };
};

const MultiplyDecimalsBonus = (props) => (
  <BonusShell
    {...props}
    title="Multiply Decimals!"
    emoji="🔟"
    sub="Multiply the decimals — then catch the product."
    intro="Multiply the decimals. Catch the correct product. Each one is worth five points. Watch out for bombs! Press Start when you are ready."
    rules={[
      { icon: "✅", text: "The product", pts: "+5" },
      { icon: "❌", text: "Wrong answer", pts: "-1" },
    ]}
    init={newProblem}
    prompt={(p) => ({ big: formatProblem(p).replace("____", "?"), small: `Catch ${ruleText()}!`, say: `What is ${p.a.val.toFixed(1)} times ${p.b.val.toFixed(1)}?` })}
    spawn={(p, onScreen) => (shouldSpawnCorrectBalanced(p, onScreen) ? p.productStr : pick(p.distractors.length ? p.distractors : ["0.1", "0.2", "0.5", "0.75"]))}
    check={(p, v) => nearlyEqual(asNumber(v), p.productNum)}
    newEvery={3}
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
  />
);

export default MultiplyDecimalsBonus;
